import { useMemo, useState, FC } from "react";
// API
import { GetAnimalById_animal } from "api/MyLivestock/Animal/types/GetAnimalById";
// Utils
import { getAnimalInfoItems } from "./helpers";
import { getFieldName } from "helpers/myLivestock";
// Components
import ConfirmMoveToGroupModal from "components/MyLivestock/Animals/AnimalBulkActions/ConfirmMoveToGroupModal";
import ConfirmMoveToFieldModal from "components/MyLivestock/Animals/AnimalBulkActions/ConfirmMoveToFieldModal";
import { Button, Text, Flex, KeyValuePair, Pill, Spacer } from "components";
import { Tag } from "tags/components/Tag";
import { useFormatDate } from "hooks";

import styles from "./AnimalInfoBlock.module.scss";

export interface AnimalInfoBlockProps {
  animal: GetAnimalById_animal;
  isArchived: boolean;
  canAddToGroup: boolean;
}

export const AnimalInfoBlock: FC<AnimalInfoBlockProps> = ({ animal, isArchived = false, canAddToGroup }) => {
  const [showGroupModal, setGroupModalShow] = useState(false);
  const [showFieldModal, setFieldModalShow] = useState(false);
  const { getFormatDate } = useFormatDate();
  const animalInfo = useMemo(() => getAnimalInfoItems(animal, isArchived, getFormatDate), [animal]);

  const AnimalTags = ({ tags }) => {
    return tags.map((tag) => {
      const t = {
        tagId: tag.tagId,
        name: tag.name,
        color: tag.color,
        ownership: tag.schema.ownership,
      };

      return (
        <Flex key={tag.tagId} item>
          <Tag tag={t} />
        </Flex>
      );
    });
  };
  return (
    <>
      <Spacer baselineHeight={2} />
      <Flex container>
        {animalInfo.map((infoCol, index) => {
          return (
            <Flex container containerDirection="column" item itemGutter xs={12} m={6} xl={3} key={index}>
              {infoCol.map(
                (item) =>
                  !item.isHidden && (
                    <KeyValuePair key={item.label} label={item.label} unit={item.unit} value={item.value} />
                  ),
              )}
            </Flex>
          );
        })}
      </Flex>

      <Spacer baselineHeight={2} />

      <Flex container>
        <Flex item itemGutter xs={12} m={6} xl={3}>
          <KeyValuePair
            label="Group"
            value={
              <Flex container>
                <Flex item>
                  <Pill
                    caption={animal?.group ? animal?.group.name : "No group"}
                    colour={animal?.group ? "yellow" : "grey"}
                    wrap
                  />
                </Flex>
                {!isArchived && (
                  <Flex item itemGutter>
                    <Button
                      caption={animal?.group ? "change the group" : "add to group"}
                      variant="ghost"
                      onClick={(): void | false => canAddToGroup && setGroupModalShow(true)}
                    />
                  </Flex>
                )}
              </Flex>
            }
            stacked
          />
        </Flex>
        <Flex item itemGutter xs={12} m={6} xl={3}>
          <KeyValuePair
            label={"location"}
            value={
              <Flex container>
                <Flex item>
                  <Pill
                    caption={
                      animal?.field
                        ? getFieldName({ fieldName: animal.field.name, unitName: animal.field.location?.unitName })
                        : "No location"
                    }
                    colour={animal?.field ? "yellow" : "grey"}
                    wrap
                  />
                </Flex>
                {!isArchived && (
                  <Flex item itemGutter>
                    <Button caption="move" variant="ghost" onClick={(): void => setFieldModalShow(true)} />
                  </Flex>
                )}
              </Flex>
            }
            stacked
          />
        </Flex>
        <Flex item itemGutter xs={12} m={6} xl={3}>
          <Text className={styles.tags_title}>Tags:</Text>
          <Flex container containerDirection="row" xs={12} m={6} xl={3}>
            <AnimalTags tags={animal?.tags} />
          </Flex>
        </Flex>
      </Flex>

      <Spacer baselineHeight={1} />

      {!isArchived && showGroupModal && (
        <ConfirmMoveToGroupModal
          animals={[animal]}
          showModal={showGroupModal}
          onClose={(): void => setGroupModalShow(false)}
          isRefetchSingleAnimalInfo
        />
      )}

      {!isArchived && showFieldModal && (
        <ConfirmMoveToFieldModal
          animalPassportId={`${animal?.passportNumber} ${animal?.visualId ? `(${animal?.visualId})` : ""}`}
          animals={[animal?.id]}
          showModal={showFieldModal}
          isRefetchSingleAnimalInfo
          onClose={(): void => setFieldModalShow(false)}
        />
      )}
    </>
  );
};
